import type { FC } from "react";
import React, { useMemo } from "react";

import { getChildrenRecord } from "../../../../../utils/react";
import { ButtonInline } from "../../../../ButtonInline";
import { BUTTON_CELL_SCHEVRON_OPTION_DISPLAY_NAME } from "../../utils/core";
import { createDisplayNameForButtonCellSchevronElement } from "../../utils/getDisplayName";
import { createTestIdForButtonCellSchevronElement } from "../../utils/getTestId";

import type { ButtonCellSchevronOptionBottomButtonsProps } from "../../types";

const FIRST_INLINE_BUTTON_TEST_ID = createTestIdForButtonCellSchevronElement(
    "first-inline-button",
);

const SECOND_INLINE_BUTTON_TEST_ID = createTestIdForButtonCellSchevronElement(
    "second-inline-button",
);

export const ButtonCellSchevronOptionInlineButton: FC<
    ButtonCellSchevronOptionBottomButtonsProps
> = ({ "data-tsid": tsid, children, ...restProps }) => {
    const {
        ButtonInlineLabel,
        ButtonInlineLeftIcon,
        ButtonInlineRightIcon,
        ButtonInlineCounter,
    } = useMemo(
        () =>
            getChildrenRecord(
                children,
                BUTTON_CELL_SCHEVRON_OPTION_DISPLAY_NAME,
                {
                    ButtonInlineLabel: {
                        required: true,
                    },
                    Unknown: {
                        skipElement: true,
                    },
                },
            ),
        [children],
    );

    return (
        <ButtonInline
            {...restProps}
            size="20"
            customColorsStyles={undefined}
            counterSize={undefined}
            data-tsid={tsid!}
        >
            <ButtonInline.Label>{ButtonInlineLabel}</ButtonInline.Label>
            {ButtonInlineRightIcon && (
                <ButtonInline.RightIcon {...ButtonInlineRightIcon.props} />
            )}
            {ButtonInlineLeftIcon && (
                <ButtonInline.LeftIcon {...ButtonInlineLeftIcon.props} />
            )}
            {ButtonInlineCounter && <ButtonInline.Counter />}
        </ButtonInline>
    );
};

export const FirstInlineButton: FC<
    ButtonCellSchevronOptionBottomButtonsProps
> = ({ "data-tsid": tsid, ...restProps }) => {
    const dataTestId = tsid || FIRST_INLINE_BUTTON_TEST_ID;

    return (
        <ButtonCellSchevronOptionInlineButton
            {...restProps}
            data-tsid={dataTestId}
        />
    );
};

FirstInlineButton.displayName =
    createDisplayNameForButtonCellSchevronElement("FirstInlineButton");

export const SecondInlineButton: FC<
    ButtonCellSchevronOptionBottomButtonsProps
> = ({ "data-tsid": tsid, ...restProps }) => {
    const dataTestId = tsid || SECOND_INLINE_BUTTON_TEST_ID;

    return (
        <ButtonCellSchevronOptionInlineButton
            {...restProps}
            data-tsid={dataTestId}
        />
    );
};

SecondInlineButton.displayName =
    createDisplayNameForButtonCellSchevronElement("SecondInlineButton");
